import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";

export default function C_E_SELECT({
  editorState,
  onSave,
  closeEditor,

  value,
  tempValue,
  onChange,
  onBlur,
  onKeyDown,
  field,
  inPopup = false,
}) {
  const properties_state = useSelector((state) => state.properties);

  // 1. Determine active data based on mode
  const activeField = inPopup ? field : editorState?.field;
  const activeValue = inPopup ? tempValue : editorState?.value;
  const isAllowOtherAnswer = inPopup
    ? activeField?.isAllowOtherAnswer
    : editorState?.isAllowOtherAnswer;

  const baseOptions = inPopup
    ? activeField?.array_templates || []
    : editorState?.options?.length > 0
      ? editorState.options
      : activeField?.array_templates || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // For popup mode

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const popupAnchorRef = useRef(null); // Reference for the popup mode anchor

  const currentAnchorEl = inPopup
    ? popupAnchorRef.current
    : editorState?.anchorEl;
  const isMenuOpen = inPopup ? isDropdownOpen : true; // In table mode, if this component is rendered, menu is open

  // 2. Auto-focus search bar when the menu opens
  useEffect(() => {
    if (isMenuOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isMenuOpen]);

  const filteredOptions = baseOptions.filter((opt) => {
    const optLabel = typeof opt === "string" ? opt : opt?.name || "";
    return optLabel.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleClose = () => {
    if (inPopup) {
      setIsDropdownOpen(false);
      if (onBlur) onBlur(); // Optional: Trigger save/blur logic on close
    } else {
      if (closeEditor) closeEditor();
    }
  };

  // 3. Mode-aware Selection Handler
  const handleSelect = (e, selectedValue) => {
    if (e) e.stopPropagation();

    if (inPopup) {
      if (onChange) onChange({ target: { value: selectedValue } });
      setSearchTerm(""); // Reset search after selection
      setIsDropdownOpen(false); // Close menu automatically
    } else {
      onSave(editorState.row.id, activeField.key, selectedValue, activeValue);
      if (closeEditor) closeEditor();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      if (filteredOptions.length > 0) {
        const firstOpt = filteredOptions[0];
        const optVal = typeof firstOpt === "string" ? firstOpt : firstOpt.name;
        handleSelect(e, optVal);
      } else if (isAllowOtherAnswer && searchTerm.trim() !== "") {
        handleSelect(e, searchTerm.trim());
      }
    } else if (inPopup && onKeyDown && !isDropdownOpen) {
      // Pass other keys (like Escape) up to the popup editor if menu is closed
      onKeyDown(e);
    }
  };

  const handleContainerKeyDown = (e) => {
    if (!containerRef.current) return;

    if (e.key === "Escape") {
      handleClose();
      return;
    }

    const focusableElements = Array.from(
      containerRef.current.querySelectorAll("input, button"),
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const currentIndex = focusableElements.indexOf(document.activeElement);

    let nextElement = null;

    if (e.key === "Tab") {
      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        nextElement = firstElement;
      } else if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        nextElement = lastElement;
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      nextElement = focusableElements[nextIndex];
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        (currentIndex - 1 + focusableElements.length) %
        focusableElements.length;
      nextElement = focusableElements[prevIndex];
    }

    if (nextElement) {
      nextElement.focus();
      nextElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  const hasExactMatch = filteredOptions.some((opt) => {
    const optLabel = typeof opt === "string" ? opt : opt?.name || "";
    return optLabel.toLowerCase() === searchTerm.trim().toLowerCase();
  });

  // --- STYLES ---
  const baseItemStyle = {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "bottom",
  };

  const flexContainerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  // --- THE SHARED MENU CONTENT ---
  const renderMenuContent = () => (
    <div
      ref={containerRef}
      onKeyDown={handleContainerKeyDown}
      style={{
        width: inPopup
          ? Math.max(250, popupAnchorRef.current?.offsetWidth || 0)
          : Math.max(250, currentAnchorEl?.offsetWidth || 0),
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
      tabIndex={-1}
    >
      {/* Hide the modal header if we are rendering inside the popup */}
      {!inPopup && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <p className="text_small" style={{ color: "#666", margin: 0 }}>
            Editing: {activeField?.name || activeField?.key}
          </p>
          <button
            className="badge_small cursor_pointer background_grey_light"
            style={{ border: "none", borderRadius: "4px", outline: "none" }}
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      )}

      {/* SEARCH INPUT */}
      <input
        ref={searchInputRef}
        type="text"
        className={
          inPopup ? "input_table margin_bottom" : "input margin_bottom"
        }
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleInputKeyDown}
        style={{
          width: "100%",
          boxSizing: "border-box",
          marginBottom: "8px",
          padding: "8px",
          borderRadius: "4px",
          border: "1px solid #ccc",
          outlineColor: "black",
        }}
      />

      {/* SCROLLABLE OPTIONS LIST */}
      <div
        className="option_list_box_dropdown"
        style={{
          maxHeight: inPopup ? "200px" : "300px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          padding: "2px",
        }}
      >
        {/* CLEAR SELECTION */}
        <React.Fragment>
          <button
            className={`badge_small cursor_pointer a11y_dropdown_item ${
              !activeValue ||
              (Array.isArray(activeValue) && activeValue.length === 0)
                ? "background_black"
                : "background_grey_light"
            }`}
            style={{
              ...baseItemStyle,
              ...flexContainerStyle,
              border: "none",
              width: "100%",
              textAlign: "left",
            }}
            onClick={(e) => handleSelect(e, "")}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                opacity: 0.8,
              }}
            >
              Clear
            </span>
          </button>
          <hr style={{ margin: "2px 0", borderTop: "1px solid #ccc" }} />
        </React.Fragment>

        {/* Base Options */}
        {filteredOptions.map((opt, idx) => {
          const optValue = typeof opt === "string" ? opt : opt.name;
          const isSelected = activeValue === optValue;

          return (
            <button
              key={idx}
              title={optValue}
              className={`badge_small cursor_pointer a11y_dropdown_item ${
                isSelected ? "background_black" : "background_grey_light"
              }`}
              style={{
                ...baseItemStyle,
                ...flexContainerStyle,
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
              onClick={(e) => handleSelect(e, optValue)}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {optValue}
              </span>
            </button>
          );
        })}

        {/* Custom Answer */}
        {isAllowOtherAnswer && searchTerm.trim() !== "" && !hasExactMatch && (
          <React.Fragment>
            {filteredOptions.length > 0 && (
              <hr style={{ margin: "2px 0", borderTop: "1px solid #ccc" }} />
            )}
            <button
              title={`Use custom answer: ${searchTerm.trim()}`}
              className="badge_small cursor_pointer background_black a11y_dropdown_item"
              style={{
                ...baseItemStyle,
                ...flexContainerStyle,
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
              onClick={(e) => handleSelect(e, searchTerm.trim())}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                + "{searchTerm.trim()}"
              </span>
            </button>
          </React.Fragment>
        )}

        {filteredOptions.length === 0 && !isAllowOtherAnswer && (
          <p
            className="text_small"
            style={{ color: "#999", textAlign: "center", marginTop: "4px" }}
          >
            No options found.
          </p>
        )}
      </div>
    </div>
  );

  if (inPopup) {
    const mode_map = properties_state.mode_map; //editor || viewer
    return (
      <div>
        {mode_map === "editor" ? (
          <main>
            <div
              ref={popupAnchorRef}
              className="input margin_bottom"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              tabIndex={0}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "#fff",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                height: "36px",
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsDropdownOpen(true);
                } else if (onKeyDown) {
                  onKeyDown(e);
                }
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {activeValue ? (
                  activeValue
                ) : (
                  <span style={{ color: "#999" }}>Select option...</span>
                )}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  flexShrink: 0,
                  marginLeft: "8px",
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>

            {/* It handles its own popup internally now! */}
            {isDropdownOpen && (
              <FLOAT_MENU
                id={`popup-select-menu-${activeField?.key}`}
                open={true}
                anchorEl={popupAnchorRef.current}
                onClose={handleClose}
              >
                {renderMenuContent()}
              </FLOAT_MENU>
            )}
          </main>
        ) : (
          <div className="container background_white text_medium">
            {activeValue}
          </div>
        )}
      </div>
    );
  }

  // --- TABLE MODE RETURN ---
  return (
    <FLOAT_MENU
      id="global-cell-editor-select"
      open={true}
      anchorEl={editorState?.anchorEl}
      onClose={handleClose}
    >
      {renderMenuContent()}
    </FLOAT_MENU>
  );
}
